import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';

function UserDetailModal({ isOpen, onClose, onUpdateUser, userData, isEditMode }) {
  const [editedUser, setEditedUser] = useState(userData);

  useEffect(() => {
    setEditedUser({
      ...userData,
      password: '', // 비밀번호 필드는 항상 비워둠 (새 비밀번호 입력용)
    });
  }, [userData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'phoneNumber') {
      // 숫자만 남기고 모든 문자 제거
      let cleanedValue = value.replace(/[^0-9]/g, '');
      let formattedNumber = '';

      if (cleanedValue.startsWith('02')) { // 서울 지역번호 (2자리)
        if (cleanedValue.length <= 2) {
          formattedNumber = cleanedValue;
        } else if (cleanedValue.length <= 6) {
          formattedNumber = `${cleanedValue.substring(0, 2)}-${cleanedValue.substring(2)}`;
        } else if (cleanedValue.length <= 10) {
          formattedNumber = `${cleanedValue.substring(0, 2)}-${cleanedValue.substring(2, 6)}-${cleanedValue.substring(6)}`;
        } else {
          formattedNumber = `${cleanedValue.substring(0, 2)}-${cleanedValue.substring(2, 6)}-${cleanedValue.substring(6, 10)}`;
        }
      } else if (cleanedValue.startsWith('0') && cleanedValue.length >= 3) { // 일반 휴대폰 번호 (3자리 시작)
        if (cleanedValue.length <= 3) {
          formattedNumber = cleanedValue;
        } else if (cleanedValue.length <= 7) {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3)}`;
        } else if (cleanedValue.length <= 11) {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3, 7)}-${cleanedValue.substring(7)}`;
        } else {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3, 7)}-${cleanedValue.substring(7, 11)}`;
        }
      } else { // 기타 (지역번호 3자리 등)
        if (cleanedValue.length <= 3) {
          formattedNumber = cleanedValue;
        } else if (cleanedValue.length <= 7) {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3)}`;
        } else if (cleanedValue.length <= 11) {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3, 7)}-${cleanedValue.substring(7)}`;
        } else {
          formattedNumber = `${cleanedValue.substring(0, 3)}-${cleanedValue.substring(3, 7)}-${cleanedValue.substring(7, 11)}`;
        }
      }
      formattedValue = formattedNumber;
    }

    // Special handling for password field: only update if value is not empty
    if (name === 'password' && value === '') {
      return; // Do not update the password field in state if the input is cleared
    }

    setEditedUser(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!editedUser.username || !editedUser.name || !editedUser.position || !editedUser.role) {
      Swal.fire('경고', '모든 필드를 입력해주세요.', 'warning');
      return;
    }

    const userToUpdate = { ...editedUser };
    // Remove authorities field as it causes deserialization error on backend
    if (userToUpdate.authorities) {
      delete userToUpdate.authorities;
    }

    // If password field is empty, set it to null so backend doesn't try to update it
    if (userToUpdate.password === '') {
      userToUpdate.password = null;
    }

    onUpdateUser(userToUpdate);
  };

  if (!isOpen) return null;

  const positions = ['대표', '전무', '이사', '부장', '과장', '대리', '직원'];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-management-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{isEditMode ? '사용자 정보 수정' : '사용자 상세 정보'}</h3>
        <form onSubmit={handleSubmit} className="form-grid">
          <div className="form-group">
            <label>사용자 이름 (ID):</label>
            <input
              type="text"
              name="username"
              value={editedUser.username}
              readOnly // 사용자 이름(ID)은 항상 변경 불가
            />
          </div>
          <div className="form-group">
            <label>이름:</label>
            <input
              type="text"
              name="name"
              value={editedUser.name}
              onChange={handleInputChange}
              required
              readOnly={!isEditMode}
            />
          </div>
          <div className="form-group">
            <label>직책:</label>
            <select name="position" value={editedUser.position} onChange={handleInputChange} required disabled={!isEditMode}>
              {positions.map(pos => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>
          </div>
          {isEditMode && (
            <div className="form-group">
              <label>새 비밀번호:</label>
              <input
                type="text"
                name="password"
                onChange={handleInputChange}
                placeholder="변경할 경우에만 입력 (생년월일 6자리)"
              />
            </div>
          )}
          <div className="form-group">
            <label>권한:</label>
            <select name="role" value={editedUser.role} onChange={handleInputChange} disabled={!isEditMode}>
              <option value="USER">USER</option>
              <option value="GUEST">GUEST</option>
              <option value="MIDADMIN">MIDADMIN</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </div>
          <div className="form-group">
            <label>이메일:</label>
            <input
              type="email"
              name="email"
              value={editedUser.email || ''}
              onChange={handleInputChange}
              readOnly={!isEditMode}
            />
          </div>
          <div className="form-group">
            <label>전화번호:</label>
            <input
              type="text"
              name="phoneNumber"
              value={editedUser.phoneNumber || ''}
              onChange={handleInputChange}
              readOnly={!isEditMode}
            />
          </div>
          <div className="form-group notes-full-width">
            <label>비고:</label>
            <textarea
              name="notes"
              value={editedUser.notes || ''}
              onChange={handleInputChange}
              readOnly={!isEditMode}
              style={{ width: '100%', minHeight: '100px' }}
            ></textarea>
          </div>
          <div className="modal-actions">
            {isEditMode && (
              <button type="submit" className="action-button">수정</button>
            )}
            <button type="button" onClick={onClose} className="action-button cancel-button">{isEditMode ? '취소' : '닫기'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserDetailModal;
